import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Trash2, Edit3, Eye, Save, Upload, ArrowLeft, Video, Image as ImgIcon, X, Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import blogAPI from '@/services/blogAPI';
import Pagination, { usePagination } from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return '—'; }
}

export function AdminBlogList() {
  const [data, setData] = useState({ posts: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const pg = usePagination(data.posts, 20);

  const load = async () => {
    setLoading(true);
    try {
      setData(await blogAPI.adminList({ status, search }));
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load();}, [status]);

  const onDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await blogAPI.adminDelete(post.id);
      toast.success('Post deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="admin-blog-list">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Blog</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">Write, publish, and manage articles for the Numuv marketing site.</p>
        </div>
        <Button onClick={() => navigate('/admin/blog/new')} data-testid="admin-blog-new-btn"><Plus className="w-4 h-4 mr-1" /> New post</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 bg-slate-100 dark:bg-zinc-900 rounded-full p-1">
          {[{ v: '', l: 'All' }, { v: 'published', l: 'Published' }, { v: 'draft', l: 'Drafts' }].map((t) => (
            <button key={t.v} onClick={() => setStatus(t.v)} className={`px-3 py-1.5 rounded-full text-[13px] font-medium transition ${status === t.v ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-zinc-500'}`} data-testid={`admin-blog-tab-${t.v || 'all'}`}>{t.l}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-full px-3 py-1.5">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title…" className="bg-transparent outline-none text-[13px] text-slate-900 dark:text-white placeholder:text-slate-400 w-48" data-testid="admin-blog-search" />
        </form>
      </div>

      <ScrollHint>
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg overflow-x-auto">
        {loading ? (
          <p className="p-8 text-center text-slate-400 text-sm">Loading…</p>
        ) : data.posts.length === 0 ? (
          <p className="p-8 text-center text-slate-500 dark:text-zinc-500 text-sm">No posts yet. Click <b>New post</b> to start.</p>
        ) : (
          <table className="w-full text-sm min-w-[720px]">
            <thead className="border-b border-slate-100 dark:border-zinc-800 text-slate-500 dark:text-zinc-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Views</th>
                <th className="text-left px-4 py-3">Updated</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pg.pageItems.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 dark:border-zinc-900 last:border-0 hover:bg-slate-50 dark:hover:bg-zinc-950" data-testid={`admin-blog-row-${p.slug}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 dark:text-white">{p.title}</div>
                    <div className="text-[11px] text-slate-400 dark:text-zinc-600 font-mono truncate max-w-[420px]">/blog/{p.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    {p.published ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 text-[11px] font-medium">Published</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-zinc-900 text-slate-500 dark:text-zinc-400 px-2 py-0.5 text-[11px] font-medium">Draft</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-zinc-400 font-mono">{p.view_count || 0}</td>
                  <td className="px-4 py-3 text-slate-500 dark:text-zinc-500 text-[12.5px]">{fmtDate(p.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {p.published && (
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="grid place-items-center h-8 w-8 rounded hover:bg-slate-100 dark:hover:bg-zinc-900 text-slate-500 dark:text-zinc-400" title="View public" data-testid={`admin-blog-view-${p.slug}`}><Eye className="w-3.5 h-3.5" /></a>
                      )}
                      <Link to={`/admin/blog/${p.id}`} className="grid place-items-center h-8 w-8 rounded hover:bg-slate-100 dark:hover:bg-zinc-900 text-slate-500 dark:text-zinc-400" title="Edit" data-testid={`admin-blog-edit-${p.slug}`}><Edit3 className="w-3.5 h-3.5" /></Link>
                      <button onClick={() => onDelete(p)} className="grid place-items-center h-8 w-8 rounded hover:bg-red-50 dark:hover:bg-red-950 text-red-500" title="Delete" data-testid={`admin-blog-delete-${p.slug}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </ScrollHint>
      <Pagination {...pg.paginationProps} testIdPrefix="admin-blog-pg" />
    </div>
  );
}

const EMPTY = {
  title: '', subtitle: '', slug: '', cover_image: '',
  content: '', tags: [], seo_title: '', seo_description: '', published: false,
};

export function AdminBlogEditor() {
  const { postId } = useParams();
  const isNew = !postId || postId === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    blogAPI.adminGet(postId)
      .then((p) => setForm({ ...EMPTY, ...p, tags: p.tags || [] }))
      .catch(() => { toast.error('Failed to load post'); navigate('/admin/blog'); })
      .finally(() => setLoading(false));
  }, [postId, isNew, navigate]);

  const patch = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === 'object' && v?.target ? v.target.value : v }));

  const insertAtCaret = (text) => {
    const ta = textareaRef.current;
    if (!ta) { setForm((f) => ({ ...f, content: (f.content || '') + text })); return; }
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const next = ta.value.slice(0, start) + text + ta.value.slice(end);
    setForm((f) => ({ ...f, content: next }));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const uploadMedia = async (file, { asCover = false } = {}) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await blogAPI.adminUploadMedia(file);
      const isVideo = res.kind === 'video';
      if (asCover) {
        setForm((f) => ({ ...f, cover_image: res.url }));
        toast.success('Cover image uploaded');
      } else {
        const snippet = isVideo
          ? `\n\n<video src="${res.url}" controls style="max-width:100%;border-radius:14px;"></video>\n\n`
          : `\n\n<img src="${res.url}" alt="" style="max-width:100%;border-radius:14px;" />\n\n`;
        insertAtCaret(snippet);
        toast.success(`${isVideo ? 'Video' : 'Image'} inserted`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const insertYouTube = () => {
    const url = window.prompt('YouTube or Vimeo URL:');
    if (!url) return;
    let embed = url;
    const ytMatch = url.match(/(?:youtu\.be\/|v=)([\w-]{11})/);
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (ytMatch) embed = `https://www.youtube.com/embed/${ytMatch[1]}`;
    else if (vimeoMatch) embed = `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    const snippet = `\n\n<div style="position:relative;padding-bottom:56.25%;height:0;border-radius:14px;overflow:hidden;"><iframe src="${embed}" style="position:absolute;inset:0;width:100%;height:100%;border:0;" allowfullscreen loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"></iframe></div>\n\n`;
    insertAtCaret(snippet);
    toast.success('Video embed inserted');
  };

  const addTag = () => {
    const v = tagInput.trim().toLowerCase();
    if (!v) return;
    if (!form.tags.includes(v)) setForm((f) => ({ ...f, tags: [...f.tags, v] }));
    setTagInput('');
  };

  const save = async (publishOverride) => {
    if (!form.title || form.title.length < 3) { toast.error('Title is required (min 3 chars)'); return; }
    if (!form.content || form.content.length < 1) { toast.error('Add some content first'); return; }
    setSaving(true);
    const payload = {
      title: form.title, subtitle: form.subtitle || '',
      slug: form.slug || '', cover_image: form.cover_image || '',
      content: form.content, tags: form.tags || [],
      seo_title: form.seo_title || '', seo_description: form.seo_description || '',
      published: typeof publishOverride === 'boolean' ? publishOverride : !!form.published,
    };
    try {
      const res = isNew ? await blogAPI.adminCreate(payload) : await blogAPI.adminUpdate(form.id || postId, payload);
      toast.success(payload.published ? 'Published' : 'Saved as draft');
      if (isNew) navigate(`/admin/blog/${res.id}`, { replace: true });
      else setForm({ ...EMPTY, ...res, tags: res.tags || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const readingTime = useMemo(() => {
    const words = (form.content || '').replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 220));
  }, [form.content]);

  if (loading) return <p className="text-center py-16 text-slate-400 text-sm">Loading…</p>;

  return (
    <div data-testid="admin-blog-editor">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <Link to="/admin/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white"><ArrowLeft className="w-3.5 h-3.5" /> Back to posts</Link>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => save(false)} disabled={saving} data-testid="admin-blog-save-draft">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save draft
          </Button>
          <Button size="sm" onClick={() => save(true)} disabled={saving} data-testid="admin-blog-publish">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />} {form.published ? 'Update' : 'Publish'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        <div className="space-y-4">
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4">
            <input value={form.title} onChange={patch('title')} placeholder="Post title…" className="w-full bg-transparent outline-none text-2xl md:text-3xl font-bold text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-zinc-700" data-testid="admin-blog-title" />
            <textarea value={form.subtitle} onChange={patch('subtitle')} placeholder="Subtitle / dek (optional)…" rows={2} className="mt-3 w-full bg-transparent outline-none text-slate-600 dark:text-zinc-400 text-[14.5px] resize-none" data-testid="admin-blog-subtitle" />
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[12px] text-slate-400">/blog/</span>
              <input value={form.slug} onChange={patch('slug')} placeholder="auto-from-title" className="flex-1 bg-transparent outline-none text-[12.5px] font-mono text-slate-500 dark:text-zinc-500" data-testid="admin-blog-slug" />
              <span className="text-[11px] text-slate-400">~{readingTime} min read</span>
            </div>
          </div>

          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <input type="file" ref={fileInputRef} accept="image/*,video/*" className="hidden" onChange={(e) => uploadMedia(e.target.files?.[0])} data-testid="admin-blog-media-input" />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="admin-blog-upload-image">
                <ImgIcon className="w-3.5 h-3.5 mr-1" /> Image / GIF
              </Button>
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="admin-blog-upload-video">
                <Upload className="w-3.5 h-3.5 mr-1" /> Video file
              </Button>
              <Button variant="outline" size="sm" onClick={insertYouTube} data-testid="admin-blog-embed-video">
                <Video className="w-3.5 h-3.5 mr-1" /> Embed YouTube/Vimeo
              </Button>
              {uploading && <span className="text-[12px] text-[#6D5EF7] inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</span>}
            </div>
            <textarea
              ref={textareaRef}
              value={form.content} onChange={patch('content')}
              placeholder={'Write your post here…\n\nSupports markdown OR HTML.\n\nUse the buttons above to insert media.'}
              rows={22}
              className="w-full bg-transparent outline-none text-[14.5px] leading-[24px] text-slate-800 dark:text-zinc-200 font-mono resize-y"
              data-testid="admin-blog-content"
            />
          </div>
        </div>

        <aside className="space-y-4">
          <Section title="Cover image">
            {form.cover_image ? (
              <div className="relative rounded-lg overflow-hidden">
                <img src={form.cover_image} alt="cover" className="w-full h-32 object-cover" />
                <button onClick={() => patch('cover_image')('')} className="absolute top-2 right-2 grid place-items-center h-7 w-7 rounded-full bg-black/60 text-white" data-testid="admin-blog-cover-remove"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <>
                <input type="file" ref={coverInputRef} accept="image/*" className="hidden" onChange={(e) => uploadMedia(e.target.files?.[0], { asCover: true })} data-testid="admin-blog-cover-input" />
                <Button variant="outline" size="sm" onClick={() => coverInputRef.current?.click()} disabled={uploading} className="w-full" data-testid="admin-blog-cover-upload">
                  <Upload className="w-3.5 h-3.5 mr-1" /> Upload cover
                </Button>
              </>
            )}
          </Section>
          <Section title="Tags">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11.5px] bg-[#6D5EF7]/10 text-[#6D5EF7]" data-testid={`admin-blog-tag-${t}`}>
                  {t}
                  <button onClick={() => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }))} aria-label="Remove tag"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="add tag…" className="flex-1 rounded-lg border border-slate-200 dark:border-zinc-800 bg-transparent px-2.5 py-1.5 text-[12.5px] outline-none focus:border-[#6D5EF7] text-slate-800 dark:text-zinc-200" data-testid="admin-blog-tag-input" />
              <Button size="sm" onClick={addTag} data-testid="admin-blog-tag-add">Add</Button>
            </div>
          </Section>
          <Section title="SEO">
            <input value={form.seo_title} onChange={patch('seo_title')} placeholder="SEO title (≤70 chars)" maxLength={70} className="w-full rounded-lg border border-slate-200 dark:border-zinc-800 bg-transparent px-2.5 py-1.5 text-[12.5px] outline-none focus:border-[#6D5EF7] text-slate-800 dark:text-zinc-200" data-testid="admin-blog-seo-title" />
            <textarea value={form.seo_description} onChange={patch('seo_description')} placeholder="Meta description (≤170 chars)" maxLength={170} rows={3} className="mt-2 w-full rounded-lg border border-slate-200 dark:border-zinc-800 bg-transparent px-2.5 py-1.5 text-[12.5px] outline-none focus:border-[#6D5EF7] text-slate-800 dark:text-zinc-200 resize-none" data-testid="admin-blog-seo-desc" />
          </Section>
          <Section title="Publishing">
            <label className="flex items-center justify-between text-[13px] text-slate-700 dark:text-zinc-300">
              <span>Published</span>
              <input type="checkbox" checked={!!form.published} onChange={(e) => patch('published')(e.target.checked)} data-testid="admin-blog-published-toggle" />
            </label>
            {!isNew && form.published && (
              <a href={`/blog/${form.slug}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[12px] text-[#6D5EF7]" data-testid="admin-blog-view-public">
                <Eye className="w-3 h-3" /> View public
              </a>
            )}
          </Section>
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4">
      <p className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-zinc-500 mb-3">{title}</p>
      {children}
    </div>
  );
}
